export const PRODUCT_VIEW_LAYER = {
  MASTER: 'master',
  STAGING: 'staging',
  LIVE: 'live',
};

export {
  getListedChannelEntries as getListingChannels,
  productNeedsTargetsModal,
  buildListingToMasterMap,
} from '../../../store/selectors/productSelectors';

export const isDraftListingLink = (link) => link?.isDraft === true;

export const isLiveListingLink = (link) => link?.isDraft !== true;

const matchesListingLayer = (link, layer) => {
  if (layer === PRODUCT_VIEW_LAYER.STAGING) return isDraftListingLink(link);
  if (layer === PRODUCT_VIEW_LAYER.LIVE) return isLiveListingLink(link);
  return true;
};

/**
 * Gallery assets for a product view layer (master / listing staging / listing live).
 */
export const filterGalleryAssets = (assets = [], { productId, layer }) => {
  if (!productId) return [];

  return assets.filter((asset) => (
    (asset.products || []).some((link) => (
      String(link._id) === String(productId)
      && (layer === PRODUCT_VIEW_LAYER.MASTER || matchesListingLayer(link, layer))
    ))
  ));
};

/**
 * A view layer edits/reads the draft customOrder layer only in listing staging; master and live
 * use the no-flag ("absent = live") layer.
 */
export const isDraftOrderLayer = (layer) => layer === PRODUCT_VIEW_LAYER.STAGING;

const matchesCustomOrderLayer = (entry, productId, layer) => (
  String(entry?.productId) === String(productId)
  && (isDraftOrderLayer(layer) ? entry?.isDraft === true : entry?.isDraft !== true)
);

/**
 * Finds an asset's gallery customOrder entry for a given product view layer.
 */
export const findGalleryOrderEntry = (asset, productId, layer) => (
  (asset?.customOrder || []).find((entry) => matchesCustomOrderLayer(entry, productId, layer))
);

/**
 * Sorts gallery assets by their layer-specific customOrder index. Assets without an entry for the
 * layer are placed at the end. The order is resolved on the client per layer because a single
 * search response carries both draft and live customOrder entries.
 */
export const sortGalleryAssetsByLayer = (assets = [], { productId, layer }) => {
  if (!productId) return assets;

  return [...assets].sort((left, right) => {
    const leftIndex = findGalleryOrderEntry(left, productId, layer)?.index ?? Number.MAX_SAFE_INTEGER;
    const rightIndex = findGalleryOrderEntry(right, productId, layer)?.index ?? Number.MAX_SAFE_INTEGER;
    return leftIndex - rightIndex;
  });
};

/**
 * Variant-specific assets for a product view layer.
 */
export const filterVariantAssets = (assets = [], {
  linkVariantId,
  variantType,
  layer,
}) => {
  if (!linkVariantId || !variantType) return [];

  return assets.filter((asset) => (
    (asset.productVariants || []).some((link) => (
      String(link.variantId) === String(linkVariantId)
      && link.type === variantType
      && (layer === PRODUCT_VIEW_LAYER.MASTER || matchesListingLayer(link, layer))
    ))
  ));
};

/**
 * Returns asset ids linked to a variant via asset.productVariants[].
 */
export const getVariantAssetIdsFromAssets = (assets = [], variantId) => (
  assets
    .filter((asset) => (
      (asset.productVariants || []).some(
        (link) => String(link.variantId) === String(variantId),
      )
    ))
    .map((asset) => String(asset._id))
);

export const resolveMasterIdFromAssetProduct = (
  productRef,
  masterProducts = [],
  listingToMasterMap = {},
) => {
  if (!productRef?._id) return null;

  const directMaster = masterProducts.find(({ _id }) => String(_id) === String(productRef._id));
  if (directMaster) return directMaster._id;

  const mappedMasterId = listingToMasterMap[productRef._id];
  if (mappedMasterId) return mappedMasterId;

  if (productRef.type === 'listing' && productRef.title) {
    const titleMatch = masterProducts.find(({ title }) => title === productRef.title);
    if (titleMatch) return titleMatch._id;
  }

  return null;
};

export const buildDetachTargetsPayload = ({
  productId,
  targets,
}) => {
  const apiTargets = [];

  if (targets.masterGallery) {
    apiTargets.push({ type: 'masterGallery', productId });
  }

  (targets.masterVariantIds || []).forEach((variantId) => {
    apiTargets.push({ type: 'masterVariant', productId, variantId });
  });

  (targets.listings || []).forEach((listing) => {
    const isDraft = listing.isDraft !== false;

    if (listing.listingGallery) {
      apiTargets.push({
        type: 'listingGallery',
        listingId: listing.listingId,
        isDraft,
        listingGallery: listing.listingGallery !== false,
      });
    }
    (listing.listingVariantIds || []).forEach((variantId) => {
      apiTargets.push({
        type: 'listingVariant',
        listingId: listing.listingId,
        variantId,
        isDraft,
      });
    });
  });

  return apiTargets;
};
